// src/client/lib/queryClient.ts
import { QueryClient, QueryKey } from "@tanstack/react-query";
import { ApiClient } from "@client/lib/api-client";
import type { ApiError } from "@client/types";

// ============================================================================
// Types
// ============================================================================

type UnauthorizedBehavior = "returnNull" | "throw";

// ============================================================================
// Error Handling Utilities
// ============================================================================

function isAuthError(error: unknown): boolean {
  if (error instanceof Error) {
    return error.message.includes("401") || error.message.includes("Unauthorized");
  }
  return false;
}

function createApiError(error: unknown): ApiError {
  if (error instanceof Error) {
    // Try to extract status from error message (e.g., "401: Unauthorized")
    const statusMatch = error.message.match(/^(\d{3}):/);
    const status = statusMatch ? parseInt(statusMatch[1]) : 500;
    
    return {
      status,
      message: error.message,
      details: error
    };
  }
  
  return {
    status: 500,
    message: "An unknown error occurred",
    details: error
  };
}

// ============================================================================
// Query Function Factory
// ============================================================================

/**
 * Creates a type-safe query function with custom 401 handling
 * 
 * @example
 * const { data } = useQuery({
 *   queryKey: ['/users'],
 *   queryFn: getQueryFn({ on401: 'returnNull' })
 * });
 */
export function getQueryFn<T>(options: {
  on401: UnauthorizedBehavior;
}) {
  return async ({ queryKey }: { queryKey: QueryKey }): Promise<T | null> => {
    try {
      const endpoint = queryKey[0] as string;
      return await ApiClient.get<T>(endpoint);
    } catch (error) {
      if (isAuthError(error)) {
        if (options.on401 === "returnNull") {
          return null;
        }
      }
      throw error;
    }
  };
}

// ============================================================================
// Default Query Function
// ============================================================================

/**
 * Default query function for React Query
 * Automatically uses ApiClient for all requests
 */
async function defaultQueryFn({ queryKey }: { queryKey: QueryKey }): Promise<unknown> {
  const endpoint = queryKey[0] as string;
  
  // Validate endpoint
  if (!endpoint || typeof endpoint !== 'string') {
    throw new Error('Invalid query key: first element must be a string endpoint');
  }
  
  try {
    return await ApiClient.get(endpoint);
  } catch (error) {
    // Convert to standardized ApiError
    throw createApiError(error);
  }
}

// ============================================================================
// Query Client Configuration
// ============================================================================

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Default query function uses ApiClient
      queryFn: defaultQueryFn,
      
      // Retry configuration
      retry: (failureCount, error) => {
        // Don't retry on auth errors
        if (isAuthError(error)) {
          return false;
        }
        
        // Don't retry on client errors (4xx)
        if (typeof error === 'object' && error !== null && 'status' in error) {
          const status = (error as ApiError).status;
          if (status >= 400 && status < 500) {
            return false;
          }
        }
        
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Stale time configuration
      staleTime: 30000, // 30 seconds - data is fresh for 30s
      
      // Refetch configuration
      refetchOnWindowFocus: false, // Don't refetch on window focus by default
      refetchOnReconnect: true,    // Refetch when reconnecting
      refetchInterval: false,       // No polling by default
      
      // Error handling
      throwOnError: false, // Errors are returned in query state, not thrown
    },
    mutations: {
      // Retry configuration for mutations
      retry: (failureCount, error) => {
        // Never retry auth errors
        if (isAuthError(error)) {
          return false;
        }
        
        // Never retry client errors
        if (typeof error === 'object' && error !== null && 'status' in error) {
          const status = (error as ApiError).status;
          if (status >= 400 && status < 500) {
            return false;
          }
        }
        
        // Retry once for network errors
        return failureCount < 1;
      },
      retryDelay: 1000,
      
      // Error handling
      throwOnError: false,
    },
  },
});

// ============================================================================
// Query Client Utilities
// ============================================================================

/**
 * Clear all queries from the cache
 */
export function clearAllQueries() {
  queryClient.clear();
}

/**
 * Invalidate queries matching a pattern
 */
export function invalidateQueriesMatching(pattern: string) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const firstKey = query.queryKey[0];
      return typeof firstKey === 'string' && firstKey.includes(pattern);
    }
  });
}

/**
 * Remove queries matching a pattern
 */
export function removeQueriesMatching(pattern: string) {
  queryClient.removeQueries({
    predicate: (query) => {
      const firstKey = query.queryKey[0];
      return typeof firstKey === 'string' && firstKey.includes(pattern);
    }
  });
}

/**
 * Check if a query is cached
 */
export function isQueryCached(queryKey: QueryKey): boolean {
  const query = queryClient.getQueryState(queryKey);
  return query !== undefined && query.data !== undefined;
}

/**
 * Get cached query data
 */
export function getCachedQueryData<T>(queryKey: QueryKey): T | undefined {
  return queryClient.getQueryData<T>(queryKey);
}

/**
 * Set query data in cache
 */
export function setQueryData<T>(queryKey: QueryKey, data: T | ((oldData: T | undefined) => T)) {
  queryClient.setQueryData<T>(queryKey, data);
}